import { router } from "@inertiajs/react"
import { Command } from "cmdk"
import { Building2, Cable, FileText, FolderOpen, KeyRound, Sparkles, User } from "lucide-react"
import { useEffect } from "react"
import { useAuth, usePermissions, useProjectPermissions } from "@/hooks/use-permissions"
import { useTranslation } from "@/hooks/use-translation"
import { ADMIN_MENU_GROUPS } from "@/lib/admin-menu"
import { WORKSPACE_NAV_GROUPS, filterWorkspaceNavItemsByPermissions } from "@/lib/workspace-nav"
import { filterNavGroupsBySections } from "@/lib/project-types"
import { cn } from "@/lib/utils"

type Item = { label: string; href: string; icon?: React.ReactNode }

export function CommandPalette({
  open,
  onOpenChange,
  projectId,
  superAdmin,
  allowedSections,
  canCreateProjects,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  projectId?: number
  superAdmin?: boolean
  allowedSections?: readonly string[]
  canCreateProjects?: boolean
}) {
  const { t } = useTranslation(["layout", "common"])
  const hasCompany = Boolean(useAuth()?.company)

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault()
        onOpenChange(!open)
      }
    }
    window.addEventListener("keydown", down)
    return () => window.removeEventListener("keydown", down)
  }, [open, onOpenChange])

  const base: Item[] = [
    { label: t("commandPalette.projects"), href: "/", icon: <FolderOpen className="h-4 w-4" /> },
    ...(canCreateProjects
      ? [{ label: t("commandPalette.newProject"), href: "/projects/new", icon: <Sparkles className="h-4 w-4" /> }]
      : []),
    { label: t("commandPalette.profile"), href: "/account/profile", icon: <User className="h-4 w-4" /> },
    ...(hasCompany
      ? [{ label: t("commandPalette.about"), href: "/company/overview", icon: <Building2 className="h-4 w-4" /> }]
      : []),
    { label: t("commandPalette.mcp"), href: "/account/mcp", icon: <KeyRound className="h-4 w-4" /> },
    // Супер-админу эти разделы приходят группой «Администрирование» ниже.
    ...(superAdmin
      ? []
      : [
          {
            label: t("shell.studio.providerConnections"),
            href: "/studio/provider-connections",
            icon: <Cable className="h-4 w-4" />,
          },
          {
            label: t("commandPalette.systemPrompts"),
            href: "/studio/system-prompts",
            icon: <FileText className="h-4 w-4" />,
          },
        ]),
  ]

  const projectPermissions = useProjectPermissions()
  const companyPermissions = usePermissions()
  const canSeeGenerations = Boolean(superAdmin) || companyPermissions.can_view_monitoring

  const workspaceGroups = projectId
    ? filterNavGroupsBySections(
        filterWorkspaceNavItemsByPermissions(WORKSPACE_NAV_GROUPS, projectPermissions, canSeeGenerations),
        allowedSections,
      ).map((g) => ({
        heading: t(g.labelKey, { defaultValue: g.id }),
        items: g.items.map((it) => ({
          label: t(it.labelKey, { defaultValue: it.href }),
          href:
            it.href === "studio" && !superAdmin
              ? `/projects/${projectId}/workspace/studio?view=assembly`
              : `/projects/${projectId}/workspace/${it.href}`,
          icon: it.icon,
        })),
      }))
    : []

  const adminHeading = t("shell.admin.title")
  const adminItems = superAdmin
    ? ADMIN_MENU_GROUPS.flatMap((group) =>
        group.items.map((item) => ({
          ...item,
          label: t(item.labelKey, { defaultValue: item.labelKey }),
          keywords: [t(group.titleKey, { defaultValue: group.titleKey }), item.keywords],
        })),
      )
    : []

  if (!open) return null

  return (
    <div
      className="pointer-events-auto fixed inset-0 z-[200] flex items-start justify-center bg-[var(--ui-overlay)] px-4 pt-[15vh] backdrop-blur-sm"
      role="dialog"
      aria-modal
      onMouseDown={() => onOpenChange(false)}
    >
      <Command
        className={cn(
          "w-full max-w-xl overflow-hidden rounded-lg border border-ui-border shadow-2xl dark:bg-zinc-950 dark:shadow-violet-950/40 light:bg-ui-bg-elevated light:shadow-[var(--shadow-sm)]",
        )}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="border-b border-ui-border px-3 py-2 text-xs text-ui-text-muted">{t("commandPalette.hint")}</div>
        <Command.Input
          placeholder={t("commandPalette.placeholder")}
          className="w-full border-0 bg-transparent px-4 py-3 text-sm text-ui-text-primary outline-none placeholder:text-ui-text-secondary"
        />
        <Command.List className="max-h-72 overflow-auto p-2">
          <Command.Empty className="px-3 py-6 text-center text-sm text-ui-text-muted">
            {t("commandPalette.empty")}
          </Command.Empty>
          <Command.Group
            heading={t("commandPalette.general")}
            className="px-2 py-1 text-[11px] font-semibold tracking-wider text-ui-text-muted uppercase"
          >
            {base.map((i) => (
              <Command.Item
                key={i.href}
                value={i.label}
                onSelect={() => {
                  router.visit(i.href)
                  onOpenChange(false)
                }}
                className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm text-ui-text-primary aria-selected:bg-ui-accent/25"
              >
                {i.icon}
                {i.label}
              </Command.Item>
            ))}
          </Command.Group>
          {workspaceGroups.map((grp) => (
            <Command.Group
              key={grp.heading}
              heading={grp.heading}
              className="px-2 py-1 text-[11px] font-semibold tracking-wider text-ui-text-muted uppercase"
            >
              {grp.items.map((i) => {
                const Icon = i.icon
                return (
                  <Command.Item
                    key={i.href}
                    value={`${grp.heading} ${i.label}`}
                    onSelect={() => {
                      router.visit(i.href)
                      onOpenChange(false)
                    }}
                    className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm text-ui-text-primary aria-selected:bg-ui-accent/25"
                  >
                    <Icon className="h-4 w-4 shrink-0 opacity-70" />
                    {i.label}
                  </Command.Item>
                )
              })}
            </Command.Group>
          ))}
          {adminItems.length > 0 ? (
            <Command.Group
              heading={adminHeading}
              className="px-2 py-1 text-[11px] font-semibold tracking-wider text-ui-text-muted uppercase"
            >
              {adminItems.map(({ href, label, keywords, icon: Icon, fullPage }) => (
                <Command.Item
                  key={href}
                  value={`${adminHeading} ${label}`}
                  keywords={keywords}
                  onSelect={() => {
                    onOpenChange(false)
                    if (fullPage) window.location.assign(href)
                    else router.visit(href)
                  }}
                  className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm text-ui-text-primary aria-selected:bg-ui-accent/25"
                >
                  <Icon className="h-4 w-4 shrink-0 opacity-70" />
                  {label}
                </Command.Item>
              ))}
            </Command.Group>
          ) : null}
        </Command.List>
      </Command>
    </div>
  )
}
